import type { ProjectIssue } from '../api.js';

export type IssueReference = Pick<ProjectIssue,
  'id' | 'project_id' | 'finding_id' | 'status' | 'source' | 'priority' | 'created_at' | 'updated_at'>;

export interface IssueReferenceExportOptions {
  projectId: string;
  statusFilter: string;
  sourceFilter: 'all' | 'review' | 'manual';
  loadedRowCount: number;
}

export interface IssueReferenceExport {
  format: 'ocnexus-issue-references-v1';
  scope: 'currently_loaded_filtered_issue_rows';
  requested_project_id: string;
  requested_filters: { status: string; source: 'all' | 'review' | 'manual' };
  loaded_row_count: number;
  filtered_row_count: number;
  duplicate_canonical_ids: { id: string; row_count: number }[];
  issues: IssueReference[];
}

/** References for the supplied filtered rows only; not a project-wide inventory
 * or execution authority. Arrays preserve distinct and duplicate canonical IDs. */
export function buildIssueReferenceExport(
  rows: readonly IssueReference[],
  options: IssueReferenceExportOptions,
): IssueReferenceExport {
  const references: IssueReference[] = rows.map((issue: IssueReference) => ({
    id: issue.id,
    project_id: issue.project_id,
    finding_id: issue.finding_id,
    status: issue.status,
    source: issue.source,
    priority: issue.priority,
    created_at: issue.created_at,
    updated_at: issue.updated_at,
  }));
  const counts = new Map<string, number>();
  for (const reference of references) counts.set(reference.id, (counts.get(reference.id) ?? 0) + 1);
  const duplicates: { id: string; row_count: number }[] = [];
  for (const [id, count] of counts) if (count > 1) duplicates.push({ id, row_count: count });
  return {
    format: 'ocnexus-issue-references-v1' as const,
    scope: 'currently_loaded_filtered_issue_rows' as const,
    requested_project_id: options.projectId,
    requested_filters: { status: options.statusFilter, source: options.sourceFilter },
    loaded_row_count: options.loadedRowCount,
    filtered_row_count: references.length,
    duplicate_canonical_ids: duplicates,
    issues: references,
  };
}
